// 後台「用戶」頁，每個人「可看的專案」那一排的標籤（專案名 · 他在這個專案的角色）。
// 按下去在旁邊出現小選單（動畫師 / 視覺統籌 / 導演 / 製作），選了就直接改他在「這個專案」的角色。
// 跟右邊「指派」對話框用同一支 API（addUserToProject）、同樣的錯誤提示、同樣的重新整理方式。
// 小選單用站內現成的 OverflowMenu：鍵盤（Enter / 空白 / ↓ 打開，↑↓ 換，Esc 關）、按外面關掉、手機不超出畫面都已經有。
// 已停用的用戶：只顯示，不能按。
import { useState } from 'react';
import { Check, ChevronDown, LoaderCircle } from 'lucide-react';
import * as api from '../lib/api';
import type { UserProjectMembership, UserRole } from '../lib/api';
import { ROLE_LABEL } from '../lib/roleLabels';
import { toast } from '../lib/toast';
import Icon from './Icon';
import OverflowMenu, { type OverflowItem } from './OverflowMenu';

/** 選單裡的順序：動畫師 / 視覺統籌 / 導演 / 製作 */
export const PROJECT_ROLE_CHOICES: readonly UserRole[] = ['animator', 'visual_lead', 'director', 'admin'];

/** 「製作」那一項下面的小字：這裡的製作只管這個專案，不是全站製作 */
export const PROJECT_ADMIN_HINT = '只管這個專案';

/** 小選單的四個項目：現在的角色打勾；「製作」多一行小字；存的時候先不能再選 */
export function projectRoleMenuItems(current: UserRole, onPick: (role: UserRole) => void, busy = false): OverflowItem[] {
  return PROJECT_ROLE_CHOICES.map((role): OverflowItem => ({
    key: role,
    label: ROLE_LABEL[role],
    active: role === current,
    icon: role === current ? Check : undefined,
    disabled: busy,
    onSelect: () => onPick(role),
    badge: role === 'admin' ? <span className="user-role-menu__hint">{PROJECT_ADMIN_HINT}</span> : undefined,
  }));
}

/**
 * 把這個人在這個專案的角色改成 role（沒有成員的話就是加進去，跟指派對話框一樣，同一支 API）。
 * 成功 → 叫 onChanged 重新抓清單（等最新的清單放上畫面才結束），回 true；
 * 沒存成 → 跳錯誤通知（跟指派對話框同一句），回 false。
 */
export async function saveProjectRole(
  userId: string,
  projectId: string,
  role: UserRole,
  onChanged: () => Promise<void> | void,
): Promise<boolean> {
  try {
    await api.addUserToProject(userId, projectId, role);
    await onChanged();
    return true;
  } catch (e) {
    toast.error('專案角色沒有改成：' + (e instanceof Error ? e.message : String(e)));
    return false;
  }
}

interface Props {
  userId: string;
  tag: UserProjectMembership;
  /** 已停用的用戶：只顯示，不能按 */
  disabled?: boolean;
  /** 存好後重新抓清單 */
  onChanged: () => Promise<void> | void;
}

export default function ProjectRoleTag({ userId, tag, disabled = false, onChanged }: Props) {
  // 按下去馬上照新的角色顯示（還在存、或存好了還沒拿到新清單）；沒存成就回到後台的樣子
  const [pending, setPending] = useState<UserRole | null>(null);
  const [saving, setSaving] = useState(false);
  const role = pending ?? tag.role;
  const text = `${tag.projectName} · ${ROLE_LABEL[role]}`;

  if (disabled) {
    return <span className="user-project-tag" title={ROLE_LABEL[tag.role]}>{text}</span>;
  }

  async function pick(next: UserRole) {
    if (saving || next === role) return;
    setPending(next);
    setSaving(true);
    try {
      await saveProjectRole(userId, tag.projectId, next, onChanged);
    } finally {
      setPending(null);
      setSaving(false);
    }
  }

  return (
    <OverflowMenu
      className="user-project-wrap"
      label={`${text}，改他在這個專案的角色`}
      header={`在「${tag.projectName}」的角色`}
      align="auto"
      trigger={{
        className: 'user-project-tag user-project-tag--btn' + (saving ? ' is-saving' : ''),
        text: <>{text}<Icon icon={saving ? LoaderCircle : ChevronDown} size="xs" spin={saving} /></>,
      }}
      items={projectRoleMenuItems(role, (r) => void pick(r), saving)}
    />
  );
}
